import { supabase } from '../lib/supabase'
import type { Tour } from '../hooks/useTours'
import { calculateTourStatus } from '../utils/tourStatus'

export interface CreateTourData {
  title_en: string
  title_ar: string
  title_ur: string
  title_fa: string
  departure_date: string
  return_date: string
  duration: string
  price: string
  image: string
  route: any
  highlights_en: string[]
  highlights_ar: string[]
  highlights_ur: string[]
  highlights_fa: string[]
  description_en: string
  description_ar: string
  description_ur: string
  description_fa: string
  images?: string[]
  memories?: string[]
}

export interface UpdateTourData extends Partial<CreateTourData> {
  id: string
}

export class TourService {
  // Create a new tour
  static async createTour(tourData: CreateTourData): Promise<{ success: boolean; data?: Tour; error?: string }> {
    try {
      // Generate a unique ID for the tour
      const tourId = crypto.randomUUID()
      
      const { data, error } = await supabase
        .from('tours')
        .insert([{ ...tourData, id: tourId }])
        .select()
        .single()

      if (error) {
        return { success: false, error: error.message }
      }

      // Transform the data to match our Tour interface
      const transformedTour = this.transformSupabaseTourToTour(data)
      return { success: true, data: transformedTour }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to create tour' 
      }
    }
  }

  // Update an existing tour
  static async updateTour(tourData: UpdateTourData): Promise<{ success: boolean; data?: Tour; error?: string }> {
    try {
      const { id, ...updateData } = tourData
      
      const { data, error } = await supabase
        .from('tours')
        .update(updateData)
        .eq('id', id)
        .select()
        .single()

      if (error) {
        return { success: false, error: error.message }
      }

      const transformedTour = this.transformSupabaseTourToTour(data)
      return { success: true, data: transformedTour }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to update tour' 
      }
    }
  }

  // Soft delete a tour
  static async deleteTour(tourId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('tours')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', tourId)

      if (error) {
        return { success: false, error: error.message }
      }

      return { success: true }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to delete tour' 
      }
    }
  }

  // Permanently delete a tour (hard delete)
  static async permanentlyDeleteTour(tourId: string, imageUrls: string[] = []): Promise<{ success: boolean; error?: string }> {
    try {
      // First, delete associated images from storage if any
      if (imageUrls.length > 0) {
        const { deleteImage } = await import('../lib/storage')
        for (const url of imageUrls) {
          if (url) {
            await deleteImage(url).catch(err => {
              console.warn('Failed to delete image:', url, err)
            })
          }
        }
      }

      // Then permanently delete the tour from database
      const { error } = await supabase
        .from('tours')
        .delete()
        .eq('id', tourId)

      if (error) {
        return { success: false, error: error.message }
      }

      return { success: true }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to permanently delete tour' 
      }
    }
  }

  // Restore a soft-deleted tour
  static async restoreTour(tourId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('tours')
        .update({ deleted_at: null })
        .eq('id', tourId)

      if (error) {
        return { success: false, error: error.message }
      }

      return { success: true }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to restore tour' 
      }
    }
  }

  // Get a single tour by ID
  static async getTourById(tourId: string): Promise<{ success: boolean; data?: Tour; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('tours')
        .select('*')
        .eq('id', tourId)
        .single()

      if (error) {
        return { success: false, error: error.message }
      }

      const transformedTour = this.transformSupabaseTourToTour(data)
      return { success: true, data: transformedTour }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to fetch tour' 
      }
    }
  }

  // Transform Supabase tour data to our Tour interface
  private static transformSupabaseTourToTour(supabaseTour: any): Tour {
    return {
      id: supabaseTour.id,
      title: {
        en: supabaseTour.title_en,
        ar: supabaseTour.title_ar,
        ur: supabaseTour.title_ur,
        fa: supabaseTour.title_fa
      },
      departureDate: supabaseTour.departure_date,
      returnDate: supabaseTour.return_date,
      duration: supabaseTour.duration,
      price: supabaseTour.price,
      image: supabaseTour.image,
      route: supabaseTour.route,
      highlights: {
        en: supabaseTour.highlights_en,
        ar: supabaseTour.highlights_ar,
        ur: supabaseTour.highlights_ur,
        fa: supabaseTour.highlights_fa
      },
      description: {
        en: supabaseTour.description_en,
        ar: supabaseTour.description_ar,
        ur: supabaseTour.description_ur,
        fa: supabaseTour.description_fa
      },
      images: supabaseTour.images || [],
      memories: supabaseTour.memories || [],
      deletedAt: supabaseTour.deleted_at
    }
  }

  // Transform our Tour interface to Supabase format
  static transformTourToSupabase(tour: Tour): CreateTourData {
    return {
      title_en: tour.title.en,
      title_ar: tour.title.ar,
      title_ur: tour.title.ur,
      title_fa: tour.title.fa,
      departure_date: tour.departureDate,
      return_date: tour.returnDate,
      duration: tour.duration,
      price: tour.price,
      image: tour.image,
      route: tour.route,
      highlights_en: tour.highlights.en,
      highlights_ar: tour.highlights.ar,
      highlights_ur: tour.highlights.ur,
      highlights_fa: tour.highlights.fa,
      description_en: tour.description.en,
      description_ar: tour.description.ar,
      description_ur: tour.description.ur,
      description_fa: tour.description.fa,
      images: tour.images || [],
      memories: tour.memories || []
    }
  }
}
